import { useState, useCallback, useEffect } from 'react';
import { SelectItemProps, Response, Pagination } from '@ui/src';
import { useLazyGetPSPExternalAccountsListQuery } from '../store/services';
import { ExternalAccount } from '../types';

interface ListResponse {
  data: Response<ExternalAccount>;
}

let timeout: NodeJS.Timeout;

const useGetExternalAccounts = (query?: string) => {
  const [autocompleteList, setAutocompleteList] = useState<SelectItemProps[]>(
    [],
  );
  const [pagination, setPagination] = useState<Pagination>();
  const [searchValue, setSearchValue] = useState('');
  const [nextPageIsLoading, setNextPageIsLoading] = useState(false);

  const [getList, { isFetching, isLoading }] =
    useLazyGetPSPExternalAccountsListQuery();

  const getAutocompleteListAction = useCallback(async () => {
    const results: SelectItemProps[] = [];

    const response = (await getList(`?take=40${query || ''}`)) as ListResponse;

    if (response?.data?.data) {
      response.data.data.edges?.forEach((item) => {
        results.push({
          name: item.node?.account?.description,
          description: item?.node?.iban,
          value: item.node.id,
          suffix: item.node?.account?.fullNumber,
        });
      });

      setPagination(response?.data?.data?.pageInfo);
    }

    setAutocompleteList(results);
  }, [query]);

  const onScroll = async ({
    cursor,
    search,
  }: {
    cursor: string;
    search?: string;
  }) => {
    setNextPageIsLoading(true);

    const results = ([] as SelectItemProps[]).concat(autocompleteList);

    const response = (await getList(
      `?take=40&cursor=${cursor}${search || ''}${query || ''}`,
    )) as ListResponse;

    if (response?.data?.data) {
      response.data.data.edges?.forEach((item) => {
        if (!results?.find((account) => account.value === item.node.id)) {
          results.push({
            name: item.node?.account?.description,
            description: item?.node?.iban,
            value: item.node.id,
            suffix: item.node?.account?.fullNumber,
          });
        }
      });

      setPagination(response?.data?.data?.pageInfo);
    }

    setAutocompleteList(results);
    setNextPageIsLoading(false);
  };

  const onSearch = (search: string) => {
    clearTimeout(timeout);

    timeout = setTimeout(async () => {
      const results: SelectItemProps[] = [];

      const accountResponse = (await getList(
        `?take=40${query || ''}&accountNumber=${search}`,
      )) as ListResponse;

      if (accountResponse?.data?.data) {
        accountResponse.data.data.edges?.forEach((item) => {
          if (!results?.find((account) => account.value === item.node.id)) {
            results.push({
              name: item.node?.account?.description,
              description: item?.node?.iban,
              value: item.node.id,
              suffix: item.node?.account?.fullNumber,
            });
          }
        });

        setPagination(accountResponse?.data?.data?.pageInfo);
        setSearchValue(`&accountNumber=${search}`);
        setAutocompleteList(results);
      } else {
        const ibanResponse = (await getList(
          `?take=40${query || ''}&iban=${search}`,
        )) as ListResponse;

        if (ibanResponse?.data?.data) {
          ibanResponse.data.data.edges?.forEach((item) => {
            if (!results?.find((account) => account.value === item.node.id)) {
              results.push({
                name: item.node?.account?.description,
                description: item?.node?.iban,
                value: item.node.id,
                suffix: item.node?.account?.fullNumber,
              });
            }
          });

          setPagination(ibanResponse?.data?.data?.pageInfo);
          setSearchValue(`&iban=${search}`);
          setAutocompleteList(results);
        }
      }
    }, 1000);
  };

  const onClear = () => {
    getAutocompleteListAction();

    if (searchValue) {
      setSearchValue('');
    }
  };

  useEffect(() => {
    getAutocompleteListAction();
  }, [getAutocompleteListAction]);

  return {
    getAutocompleteListAction,
    autocompleteList,
    pagination,
    onSearch,
    isLoading: isLoading || isFetching,
    onClear,
    onScroll,
    searchValue,
    nextPageIsLoading,
    setAutocompleteList,
  };
};

export default useGetExternalAccounts;
